import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container } from "reactstrap";
import { Edit, RefreshCw } from "react-feather";
import { fetchApps } from "../../utils/appsCalls";
import Breadcrumb from "../common/breadcrumb";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { Tile, RCard, Loader as RLoader } from "../reporting/ReportingShared";

const AppsList = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [apps, setApps] = useState([]);
  const [search, setSearch] = useState("");

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchApps();
      setApps(data || []);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = apps.filter((app) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return `${app.name || ""} ${app.reference || ""}`.toLowerCase().includes(q);
  });

  const activeCount = apps.filter((a) => a.isActive).length;

  const StatusPill = ({ active }) => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "4px 10px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        background: active ? C.greenLight : C.coralLight,
        color: active ? C.green : C.coral,
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: active ? C.green : C.coral }} />
      {active ? "Active" : "Inactive"}
    </span>
  );

  return (
    <Fragment>
      <Breadcrumb title="Apps" parent="Dashboard" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 16,
              marginBottom: 24,
            }}
          >
            <Tile label="Total Apps" value={apps.length} icon="📱" gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} delay={0} />
            <Tile label="Active" value={activeCount} icon="✅" gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`} delay={60} />
          </div>

          <RCard>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <button
                onClick={fetchData}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  border: `1.5px solid ${C.border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  background: C.white,
                }}
              >
                <RefreshCw size={15} color={C.textMed} />
              </button>
              <input
                placeholder="Search app name or reference..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  flex: 1,
                  padding: "7px 12px",
                  fontSize: 12,
                  borderRadius: 8,
                  border: `1.5px solid ${C.border}`,
                  fontFamily: "inherit",
                  outline: "none",
                  color: C.text,
                  background: C.white,
                }}
              />
              <button
                onClick={() => navigate("/apps/add")}
                style={{
                  padding: "8px 16px",
                  borderRadius: 8,
                  border: "none",
                  background: C.coral,
                  color: C.white,
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                + Add App
              </button>
            </div>

            {isLoading ? (
              <RLoader />
            ) : filtered.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: C.textLight, fontSize: 13 }}>
                {apps.length === 0 ? "No apps yet." : "No apps match your search."}
              </div>
            ) : (
              <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
                <table style={tbl.table}>
                  <thead>
                    <tr>
                      <th style={tbl.th}>ID</th>
                      <th style={tbl.th}>Name</th>
                      <th style={tbl.th}>Reference</th>
                      <th style={tbl.th}>Merchants</th>
                      <th style={tbl.th}>Min / Latest Version</th>
                      <th style={tbl.th}>Status</th>
                      <th style={{ ...tbl.th, textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((app) => (
                      <tr key={app.id} className="rpt-row">
                        <td style={{ ...tbl.td, color: C.textLight, fontWeight: 600 }}>{app.id}</td>
                        <td style={tbl.td}>{app.name || "—"}</td>
                        <td style={{ ...tbl.td, fontFamily: "monospace", fontSize: 12 }}>{app.reference}</td>
                        <td style={tbl.td}>
                          {(app.merchants || []).map((m) => m.name || m.reference).join(", ") || "—"}
                        </td>
                        <td style={tbl.td}>
                          {(app.minSupportedVersion || "—") + " / " + (app.latestVersion || "—")}
                        </td>
                        <td style={tbl.td}>
                          <StatusPill active={app.isActive} />
                        </td>
                        <td style={tbl.td}>
                          <div style={{ display: "flex", justifyContent: "center", gap: 6 }}>
                            <div
                              onClick={() => navigate(`/apps/edit/${app.id}`)}
                              style={{
                                width: 32,
                                height: 32,
                                borderRadius: 8,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                border: `1px solid ${C.border}`,
                                background: C.white,
                              }}
                            >
                              <Edit size={15} color={C.green} />
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </RCard>
        </div>
      </Container>
    </Fragment>
  );
};

export default AppsList;
